import { useQuery } from '@tanstack/react-query';
import {
  fetchProducts,
  fetchProductBySlug,
  fetchProductSamples,
  fetchProductImages,
  fetchLatestProductVersion,
  fetchProductDownloads,
  fetchProductVersions,
} from '../api/productService';
import type { Product } from '../types/product';
import type { ProductImage } from '../types/productImage';
import type { ProductSample } from '../types/productSample';
import type { ProductVersion } from '../types/productVersion';
import type { ProductDownload } from '../types/productDownload';

export function useProducts() {
  return useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: fetchProducts,
  });
}

export function useProduct(slug: string) {
  return useQuery<Product>({
    queryKey: ['products', slug],
    queryFn: () => fetchProductBySlug(slug),
    enabled: !!slug,
  });
}

export function useProductSamples(productId: string) {
  return useQuery<ProductSample[]>({
    queryKey: ['product-samples', productId],
    queryFn: () => fetchProductSamples(productId),
    enabled: !!productId,
  });
}

export function useLatestProductVersion(productId: string) {
  return useQuery<ProductVersion | null>({
    queryKey: ['product-version', productId],
    queryFn: () => fetchLatestProductVersion(productId),
    enabled: !!productId,
  });
}

export function useProductImages(productId: string) {
  return useQuery<ProductImage[]>({
    queryKey: ['product-images', productId],
    queryFn: () => fetchProductImages(productId),
    enabled: !!productId,
  });
}

export function useProductDownloads(productVersionId: string) {
  return useQuery<ProductDownload[]>({
    queryKey: ['product-downloads', productVersionId],
    queryFn: () => fetchProductDownloads(productVersionId),
    enabled: !!productVersionId,
  });
}

export function useProductVersions(productId: string) {
  return useQuery<ProductVersion[]>({
    queryKey: ['product-versions', productId],
    queryFn: () => fetchProductVersions(productId),
    enabled: !!productId,
  });
}
